import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, AlertTriangle, GaugeCircle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, err: "" });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, err: "Missing verification token" });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        if (mounted) setState({ loading: false, ok: true, err: "" });
      } catch (e) {
        if (mounted) setState({ loading: false, ok: false, err: formatApiError(e) });
      }
    })();
    return () => (mounted = false);
  }, [token]);

  return (
    <div className="min-h-screen bg-surface flex flex-col justify-center items-center p-5" data-testid="verify-page">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-lg bg-navy-900 flex items-center justify-center">
          <GaugeCircle className="w-5 h-5 text-orange-500" />
        </div>
        <span className="font-bold text-lg">EntryBook</span>
      </Link>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-card border border-navy-100 p-8 text-center">
        {state.loading ? (
          <div data-testid="verify-loading">
            <div className="w-8 h-8 mx-auto border-2 border-orange-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-navy-600 text-sm">Verifying…</p>
          </div>
        ) : state.ok ? (
          <div data-testid="verify-ok">
            <div className="w-12 h-12 bg-success/15 text-success rounded-full mx-auto flex items-center justify-center mb-3">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-semibold">Email verified</h2>
            <p className="text-sm text-navy-600 mt-2">You're all set. Return to the dashboard or log in.</p>
            <Link to="/dashboard" className="btn-accent inline-flex mt-5">Go to dashboard</Link>
          </div>
        ) : (
          <div data-testid="verify-error">
            <div className="w-12 h-12 bg-danger/10 text-danger rounded-full mx-auto flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-semibold">Verification failed</h2>
            <p className="text-sm text-navy-600 mt-2">{state.err}</p>
            <Link to="/login" className="btn-outline inline-flex mt-5">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
